import { z } from "zod";

const ProjectCountSchema = z.object({
  project_id: z.number(),
  count: z.number(),
  region: z.string(),
});

const DatasetBaseSchema = z.object({
  id: z.number(),
  name: z.string(),
  type_list: z.array(z.number()),
  timestamp: z.string().nullable(),
  approved_customers: z.array(z.number()),
  non_included_customers: z.array(z.number()),
  synthetic_customers: z.array(z.number()),
  // True once a classification of a dataset-relevant type is edited; reset
  // when the dataset is rebuilt.
  modified_since_integration: z.boolean(),
  deprecated: z.boolean(),
});

const DatasetDetailExtension = {
  image_count: z.number(),
  project_counts: z.array(ProjectCountSchema),
} as const;

export const DatasetSchema = DatasetBaseSchema.extend({
  sensitive: z.boolean(),
  crop: z.boolean(),
});

export type Dataset = z.infer<typeof DatasetSchema>;

export const DatasetDetailSchema = DatasetSchema.extend(DatasetDetailExtension);

export type DatasetDetail = z.infer<typeof DatasetDetailSchema>;

export const DefectDatasetSchema = DatasetBaseSchema;

export type DefectDataset = z.infer<typeof DefectDatasetSchema>;

export const DefectDatasetDetailSchema = DefectDatasetSchema.extend(
  DatasetDetailExtension,
);

export type DefectDatasetDetail = z.infer<typeof DefectDatasetDetailSchema>;

export const DatasetImageSchema = z.object({
  id: z.number(),
  project_id: z.number(),
  name: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  src: z.string(),
  region: z.string(),
  train: z.boolean(),
  // Distinct [object type id, workflow status id] pairs on this image,
  // limited to the dataset's type_list. Arrives with the image row — no
  // separate facets request needed.
  type_x_workflow_status: z.array(z.tuple([z.number(), z.number()])),
  // True if any of the image's detections (within the dataset's type_list)
  // were touched after the dataset was last built — OR'd across detections.
  modified_since_integration: z.boolean(),
  // Defect datasets only: true if any image_object on the image is flagged
  // `object_has_no_defects` in the DB. Object-dataset payloads omit this key.
  has_object_without_defect: z.boolean().default(false),
});

export type DatasetImage = z.infer<typeof DatasetImageSchema>;

// One ImageObjectType row attached to a bounding box. `object_type_id`
// references an ObjectType (name/colour) — already cached via /object_types.
export const AnnotationTypeSchema = z.object({
  id: z.number(),
  object_type_id: z.number(),
  workflow_status: z.number().nullable(),
});

export type AnnotationType = z.infer<typeof AnnotationTypeSchema>;

// A bounding box (ImageObject). Coordinates are normalised to [0, 1]: x/y is
// the top-left corner, width/height the box size; rotation is in degrees.
export const ImageAnnotationSchema = z.object({
  id: z.number(),
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  rotation: z.number().nullable(),
  // Reviewer-flagged: this box has no defect (same flag the dataset image
  // list's `has_object_without_defect` is OR'd across all boxes from).
  object_has_no_defects: z.boolean(),
  types: z.array(AnnotationTypeSchema),
});

export type ImageAnnotation = z.infer<typeof ImageAnnotationSchema>;

// Result of reverting Supervisor Approved classifications on a dataset image.
export const UnsuperverifyResultSchema = z.object({
  updated_count: z.number(),
});

export const CropListResponseSchema = z.object({
  mapped: z.boolean(),
  paths: z.array(z.string()),
});
